/**
 * CoauthorDiagnostic - Ferramenta de diagnóstico para problemas de co-autoria
 */

import React, { useState } from 'react';
import axios from 'axios';
import { 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Wrench,
  Loader2,
  User,
  Mail,
  BookOpen
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CoauthorDiagnostic({ bookId, bookTitle, onFixed }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);

  const runDiagnostic = async () => {
    if (!email.trim()) {
      toast.error('Digite o email do co-autor');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/coautoria/debug/coauthor-status/${bookId}?email=${encodeURIComponent(email)}`,
        { withCredentials: true }
      );
      setResult(response.data);
    } catch (error) {
      console.error('Diagnostic error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao executar diagnóstico');
    } finally {
      setLoading(false);
    }
  };

  const fixProblem = async () => {
    setFixing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/coautoria/fix/coauthor/${bookId}`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const msg = response.data.author_created 
          ? 'Co-autor adicionado com sucesso!' 
          : 'Sincronização corrigida!';
        toast.success(msg);
        
        // Aguarda um momento e roda diagnóstico novamente para confirmar
        setTimeout(async () => {
          await runDiagnostic();
          toast.info('Diagnóstico atualizado. Verifique o status.');
        }, 500);
        
        if (onFixed) onFixed();
      } else {
        toast.success(response.data.message || 'Problema corrigido!');
        setResult(null);
        setEmail('');
      }
    } catch (error) {
      console.error('Fix error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao corrigir');
    } finally {
      setFixing(false);
    }
  };

  const getStatus = () => {
    if (!result) return null;

    const hasUser = !!result.user_found;
    const hasAuthorEntry = !!result.book_authors_entry;
    const authorActive = result.book_authors_entry?.status === 'active';
    const hasCollabEntry = !!result.book_collaborators_by_user_id;
    const collabAccepted = result.book_collaborators_by_user_id?.status === 'accepted';

    // Tudo OK
    if (hasUser && authorActive && collabAccepted) {
      return {
        status: 'ok',
        message: 'Tudo OK! O co-autor deveria conseguir ver o livro.',
        canFix: false
      };
    }

    // Usuário não existe no sistema
    if (!hasUser) {
      return {
        status: 'warning',
        message: 'Usuário ainda não fez login no sistema. Peça para ele acessar o app primeiro.',
        canFix: false
      };
    }

    // Convite pendente
    if (hasAuthorEntry && result.book_authors_entry?.status === 'pending') {
      return {
        status: 'warning',
        message: 'Convite ainda está pendente. O usuário precisa aceitar o convite.',
        canFix: true
      };
    }

    // Falta entry em book_collaborators
    if (hasUser && (!hasCollabEntry || !collabAccepted)) {
      return {
        status: 'error',
        message: 'Problema encontrado! Falta sincronização. Clique em "Corrigir" para resolver.',
        canFix: true
      };
    }

    return {
      status: 'unknown',
      message: 'Status desconhecido. Verifique os detalhes abaixo.',
      canFix: true
    };
  };

  const status = getStatus();

  return (
    <div className="bg-white/5 rounded-xl p-4 space-y-4 border border-white/10">
      <div className="flex items-center gap-2 text-amber-400">
        <Wrench className="h-5 w-5" />
        <h3 className="font-medium">Diagnóstico de Co-Autor</h3>
      </div>

      <p className="text-sm text-gray-400">
        Se um co-autor aceitou o convite mas não consegue ver o livro, use esta ferramenta para diagnosticar e corrigir.
      </p>

      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="Email do co-autor (ex: nome@email.com)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-white/5 border-white/10"
          data-testid="coauthor-email-input"
        />
        <Button
          onClick={runDiagnostic}
          disabled={loading || !email.trim()}
          className="bg-amber-500 hover:bg-amber-600 text-black"
          data-testid="run-diagnostic-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Verificar
            </>
          )}
        </Button>
      </div>

      {result && (
        <div className="space-y-4 pt-4 border-t border-white/10">
          {/* Status Summary */}
          {status && (
            <div className={`rounded-lg p-4 flex items-start gap-3 ${
              status.status === 'ok' ? 'bg-green-500/10 border border-green-500/20' :
              status.status === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/20' :
              status.status === 'error' ? 'bg-red-500/10 border border-red-500/20' :
              'bg-gray-500/10 border border-gray-500/20'
            }`}>
              {status.status === 'ok' && <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />}
              {status.status === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />}
              {status.status === 'error' && <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />}
              
              <div className="flex-1">
                <p className={`font-medium ${
                  status.status === 'ok' ? 'text-green-400' :
                  status.status === 'warning' ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {status.message}
                </p>
                
                {status.canFix && result.user_found && (
                  <Button
                    onClick={fixProblem}
                    disabled={fixing}
                    size="sm"
                    className="mt-3 bg-green-500 hover:bg-green-600 text-black"
                    data-testid="fix-coauthor-btn"
                  >
                    {fixing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Wrench className="h-4 w-4 mr-2" />
                    )}
                    Corrigir Problema
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <BookOpen className="h-4 w-4" />
              <span>Livro: {result.book_title}</span>
            </div>

            {result.user_found ? (
              <div className="flex items-center gap-2 text-green-400">
                <User className="h-4 w-4" />
                <span>Usuário encontrado: {result.user_found.name || result.user_found.email}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-yellow-400">
                <User className="h-4 w-4" />
                <span>Usuário não encontrado no sistema</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className={result.book_authors_entry ? 'text-green-400' : 'text-red-400'}>
                book_authors: {result.book_authors_entry?.status || 'não encontrado'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className={result.book_collaborators_by_user_id ? 'text-green-400' : 'text-red-400'}>
                book_collaborators: {result.book_collaborators_by_user_id?.status || 'não encontrado'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
